import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Skeleton,
  Snackbar,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  SmartToy as AgentIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  PowerSettingsNew as PowerIcon,
  AccountBalanceWallet as WalletIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants';
import { agentsService } from '../services/agentsService';


const Dashboard = () => {
  const navigate = useNavigate();

  // Estados para dados e loading
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalConversations: 0,
    averageResponseTime: '0s',
    successRate: 0,
    agentsGrowth: '',
    conversationsGrowth: '',
    responseTimeGrowth: '',
    successRateGrowth: '',
  });
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Novos estados para créditos e gráfico
  const [creditsData, setCreditsData] = useState(null);
  const [spendingData, setSpendingData] = useState(null);
  const [spendingPeriod, setSpendingPeriod] = useState('1m');
  const [chartLoading, setChartLoading] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);

  // Paleta de cores para os agentes
  const agentColors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', 
    '#00f2fe', '#43e97b', '#38f9d7', '#fa709a', '#fee140',
    '#a8edea', '#fed6e3', '#ffecd2', '#fcb69f', '#c471ed',
    '#f64f59', '#667db6', '#0082c8', '#667292', '#f2994a'
  ];
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Estados para o modal de confirmação de exclusão


  // Função para formatar números grandes
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Carregar dados do dashboard
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Buscar estatísticas, lista LEVE de agentes e créditos em paralelo
        const [statsData, agentsSummary, creditsInfo] = await Promise.all([
          agentsService.getDashboardStats(),
          agentsService.getAgentsSummary(),
          agentsService.getCompanyCredits(),
        ]);

        setStats(statsData);
        // Normalizar para o formato usado nos cards do dashboard
        const normalizedAgents = (agentsSummary || []).map(a => ({
          id: a.id,
          name: a.name || 'Agente',
          description: a.description || '',
          is_active: a.is_active,
          conversations: a.conversations || 0,
          tools: a.tools || [],
          llm_config: { model: a.llm_model },
        }));
        setAgents(normalizedAgents);
        setCreditsData(creditsInfo);
      } catch (err) {
        console.error('Erro ao carregar dados do dashboard:', err);
        setError('Erro ao carregar dados do dashboard. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Carregar dados de gastos por agente
  useEffect(() => {
    const loadSpendingData = async () => {
      try {
        setChartLoading(true);
        const spendingInfo = await agentsService.getAgentsSpending(spendingPeriod);
        setSpendingData(spendingInfo);
      } catch (err) {
        console.error('Erro ao carregar dados de gastos:', err);
      } finally {
        setChartLoading(false);
      }
    };

    loadSpendingData();
  }, [spendingPeriod]);

  // Configuração das estatísticas para exibição
  const statsConfig = [
    {
      title: 'Total de Agentes',
      value: stats.total_agents?.toString() || '0',
      icon: <AgentIcon />,
      color: 'primary',
      change: `${stats.active_agents || 0} ativos`,
    },
    {
      title: 'Conversas (30 dias)',
      value: stats.recent_conversations?.toString() || '0',
      icon: <TrendingUpIcon />,
      color: 'success',
      change: `${stats.total_conversations || 0} total`,
    },
    {
      title: 'Usuários Únicos',
      value: stats.unique_users?.toString() || '0',
      icon: <ScheduleIcon />,
      color: 'info',
      change: 'últimos 30 dias',
    },
    {
      title: 'Créditos Gastos',
      value: formatNumber(Math.round((stats.total_tokens_30d || 0) / 1000)),
      icon: <AssessmentIcon />,
      color: 'warning',
      change: 'últimos 30 dias',
    },
    {
      title: 'Saldo de Créditos',
      value: creditsData?.formatted_balance || '--',
      icon: <WalletIcon />,
      color: 'secondary',
      change: creditsData ? 'disponível' : 'carregando...',
    },
  ];

  const handleCreateAgent = () => {
    navigate('/agents/create');
  };

  const handleEditAgent = (agent) => {
    navigate(`/agents/${agent.id}/edit`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleToggleAgentStatus = async (agentId, currentStatus) => {
    try {
      await agentsService.toggleAgentStatus(agentId);
      
      // Atualizar o status local
      setAgents(prev => prev.map(agent => 
        agent.id === agentId 
          ? { ...agent, is_active: !currentStatus }
          : agent
      ));
      
      setSnackbar({
        open: true,
        message: `Agente ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao alterar status do agente:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao alterar status do agente. Tente novamente.',
        severity: 'error'
      });
    }
  };



  // Função para recarregar dados
  const handleRetry = () => {
    window.location.reload();
  };

  // Tela de erro
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2, display: 'inline-flex' }}
        >
          {error}
        </Alert>
        <Box>
          <Button variant="contained" onClick={handleRetry}>
            Tentar Novamente
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>

      {/* Stats Cards */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        {statsConfig.map((stat, index) => (
          loading ? (
            <Card key={`skeleton-stat-${index}`} sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="40%" height={40} sx={{ mt: 2 }} />
                <Skeleton variant="text" width="50%" height={16} />
              </CardContent>
            </Card>
          ) : (
            <Card
              key={stat.title}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${stat.color === 'primary' ? '#6E53F2 0%, #201C5A 100%' : 
                  stat.color === 'success' ? '#4facfe 0%, #00f2fe 100%' :
                  stat.color === 'info' ? '#43e97b 0%, #38f9d7 100%' :
                  stat.color === 'warning' ? '#fa709a 0%, #fee140 100%' :
                  '#667eea 0%, #764ba2 100%'})`,
                color: 'white',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {stat.title}
                  </Typography>
                  <Box sx={{ 
                    p: 0.5, 
                    borderRadius: '50%', 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {React.cloneElement(stat.icon, { sx: { fontSize: '1.2rem' } })}
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.5rem', mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {stat.change}
                </Typography>
              </CardContent>
            </Card>
          )
        ))}
      </Box>

      {/* Agents Spending Chart */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BarChartIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Gastos por Agente
              </Typography>
            </Box>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={spendingPeriod}
                onChange={(e) => setSpendingPeriod(e.target.value)}
                displayEmpty
              >
                <MenuItem value="7d">7 dias</MenuItem>
                <MenuItem value="1m">1 mês</MenuItem>
                <MenuItem value="6m">6 meses</MenuItem>
                <MenuItem value="1y">1 ano</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {chartLoading ? (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Skeleton variant="rectangular" width="100%" height={250} />
            </Box>
          ) : spendingData && spendingData.spending_data && spendingData.spending_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={spendingData.spending_data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="agent_name" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Créditos', angle: -90, position: 'insideLeft' }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Conversas', angle: 90, position: 'insideRight' }}
                />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value, name, props) => {
                    if (name === 'Créditos Gastos') {
                      return [`${value} créditos`, 'Créditos Gastos'];
                    } else if (name === 'Conversas') {
                      return [`${value} conversas`, 'Conversas'];
                    }
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="credits_spent" 
                  name="Créditos Gastos"
                  fill="#667eea"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  yAxisId="right"
                  dataKey="conversations" 
                  name="Conversas"
                  fill="#00d2a3"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nenhum dado de gastos disponível para o período selecionado
              </Typography>
            </Box>
          )}

          {spendingData && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                📊 Total: {spendingData.total_credits_spent} créditos • {spendingData.total_conversations} conversas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                📅 Período: {spendingData.period_days} dias • {spendingData.agents_count} agentes
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Agents Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Seus Agentes
          </Typography>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleCreateAgent}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.dark',
              },
              borderRadius: 1,
              px: 2,
              py: 0.5,
            }}
          >
            Criar Agente
          </Button>
        </Box>
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {loading ? (
            // Skeleton loading para agentes
            Array.from({ length: 6 }).map((_, index) => (
              <Card key={`skeleton-${index}`} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="text" width={100} height={24} />
                    </Box>
                    <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '12px' }} />
                  </Box>
                  <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="50%" height={20} sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={80} height={16} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '10px' }} />
                    <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '10px' }} />
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : agents.length === 0 ? (
            // Estado vazio
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6 }}>
              <AgentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Nenhum agente encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Crie seu primeiro agente para começar a automatizar conversas
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateAgent}
                sx={{ borderRadius: 2 }}
              >
                Criar Primeiro Agente
              </Button>
            </Box>
          ) : (
            // Lista de agentes (apenas ativos)
            agents.filter(agent => agent.is_active).map((agent, index) => (
              <Card
                key={agent.id}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleEditAgent(agent)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexGrow: 1 }}>
                      <AgentIcon color="primary" />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {agent.name || 'Agente'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleAgentStatus(agent.id, agent.is_active);
                        }}
                        sx={{ 
                          minWidth: 'auto', 
                          p: 0.5,
                          color: agent.is_active ? 'success.main' : 'text.disabled',
                          '&:hover': {
                            backgroundColor: agent.is_active ? 'success.light' : 'action.hover',
                          },
                        }}
                      >
                        <PowerIcon fontSize="small" />
                      </Button>
                      <Chip
                        label={agent.is_active ? 'Ativo' : 'Inativo'}
                        color={agent.is_active ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      height: '63px', // 3 linhas * 21px por linha
                    }}
                  >
                    {agent.description || 'Agente inteligente para automatizar conversas'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      {agent.conversations || 0} conversas
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {agent.tools?.length || 0} ferramentas
                    </Typography>
                  </Box>
                  
                  {agent.tools && agent.tools.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
                      {agent.tools.slice(0, 3).map((tool, toolIndex) => (
                        <Chip
                          key={`${agent.id}-tool-${toolIndex}`}
                          label={tool}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                      {agent.tools.length > 3 && (
                        <Chip
                          label={`+${agent.tools.length - 3}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Box>

      {/* Conversas Recentes - removida por decisão de UX */}



      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>


    </Box>
  );
};

export default Dashboard; 